export interface Currency {
  code: string;
  name: string;
  symbol: string;
  flag: string;
  locale?: string;
}

export const SUPPORTED_CURRENCIES: Currency[] = [
  {
    code: 'IDR',
    name: 'Indonesian Rupiah',
    symbol: 'Rp',
    flag: '🇮🇩',
    locale: 'id-ID',
  },
  {
    code: 'USD',
    name: 'US Dollar',
    symbol: '$',
    flag: '🇺🇸',
    locale: 'en-US',
  },
  {
    code: 'EUR',
    name: 'Euro',
    symbol: '€',
    flag: '🇪🇺',
    locale: 'de-DE',
  },
  {
    code: 'GBP',
    name: 'British Pound',
    symbol: '£',
    flag: '🇬🇧',
    locale: 'en-GB',
  },
  {
    code: 'JPY',
    name: 'Japanese Yen',
    symbol: '¥',
    flag: '🇯🇵',
    locale: 'ja-JP',
  },
  {
    code: 'SGD',
    name: 'Singapore Dollar',
    symbol: 'S$',
    flag: '🇸🇬',
    locale: 'en-SG',
  },
  {
    code: 'MYR',
    name: 'Malaysian Ringgit',
    symbol: 'RM',
    flag: '🇲🇾',
    locale: 'ms-MY',
  },
  {
    code: 'AUD',
    name: 'Australian Dollar',
    symbol: 'A$',
    flag: '🇦🇺',
    locale: 'en-AU',
  },
  {
    code: 'CAD',
    name: 'Canadian Dollar',
    symbol: 'C$',
    flag: '🇨🇦',
    locale: 'en-CA',
  },
  {
    code: 'CHF',
    name: 'Swiss Franc',
    symbol: 'CHF',
    flag: '🇨🇭',
    locale: 'de-CH',
  },
];

export const getCurrencyByCode = (code: string): Currency | undefined => {
  return SUPPORTED_CURRENCIES.find(currency => currency.code === code);
};

export const getCurrencySymbol = (code: string): string => {
  const currency = getCurrencyByCode(code);
  return currency?.symbol || code;
};

export const getCurrencyLocale = (code: string): string => {
  const currency = getCurrencyByCode(code);
  return currency?.locale || 'en-US';
};

// Resolve a currency input (code or symbol) into a supported ISO code
export const resolveCurrencyCode = (input?: string): string => {
  if (!input) return 'IDR';
  const trimmed = input.trim();
  const upper = trimmed.toUpperCase();
  // Try match by code
  const byCode = getCurrencyByCode(upper);
  if (byCode) return byCode.code;
  // Try match by symbol
  const bySymbol = SUPPORTED_CURRENCIES.find(c => c.symbol === trimmed);
  if (bySymbol) return bySymbol.code;
  // Common aliases
  if (trimmed === 'RP') return 'IDR';
  if (trimmed === '$' || upper === 'USD') return 'USD';
  if (trimmed === '€' || upper === 'EUR') return 'EUR';
  if (trimmed === '£' || upper === 'GBP') return 'GBP';
  if (trimmed === '¥' || upper === 'JPY') return 'JPY';
  // Default fallback
  return 'IDR';
};

export default SUPPORTED_CURRENCIES;
